import { Address, Country } from '@prisma/client';
import { HoldingType } from '../business-entity/enums';
import { IGovernanceStructure } from '../business-entity/types';
import { BoardPositionEnum, Gender } from './enums';

export enum GovernanceRole {
  DIRECTOR = 'DIRECTOR',
  AUTHORIZED_SIGNATORY = 'AUTHORIZED_SIGNATORY',
  // Set on every person with a beneficial-ownership record on the corporation.
  BENEFICIAL_OWNER = 'BENEFICIAL_OWNER',
}

export type GovernanceRelationshipDirector = {
  isDirector: boolean;
  boardPositions: BoardPositionEnum[];
  directorIdentificationNumber?: string;
};

export enum PersonLocationType {
  RESIDENTIAL = 'RESIDENTIAL',
  MAILING = 'MAILING',
  OPERATING = 'OPERATING',
}

// TODO: locationID - We might need an id for the location.
export type PersonLocation = {
  name?: string;
  address: Address;
  isPrimary: boolean;
  type: PersonLocationType[];
};

// WIP: will keep adding fields as we see them in the frontend.
export type PersonOnboardingInfo = {
  // Country-specific fields collected during onboarding
  countrySpecificFields: {
    // Add country-specific fields as needed
  };
};

export enum KYCType {
  // COUNTRY_ID
  US_SSN = 'US_SSN',
  // Indian kyc ids:
  IN_PAN = 'IN_PAN',
}

export type KYC_ID = {
  type: KYCType;
  name: string;
  // we don't always have the value or the document. We should always have one of them.
  // TODO(Adarsh): We should have rules around what type of KYC IDs can accept values and what not e.g. We should never have aadharNumber as string in our system.
  value?: string;
  documentId?: string; // refernce to fileId in document table
  doesNotHaveKYCId?: boolean; // if we know that the person does not have this kyc id, we can set this to true. Eg: for SSN
  decryptedValue?: string;
};

export type GovernanceRelationships = {
  [GovernanceRole.DIRECTOR]?: GovernanceRelationshipDirector;
  [GovernanceRole.AUTHORIZED_SIGNATORY]?: {
    isAuthorizedSignatory: boolean;
  };
  [GovernanceRole.BENEFICIAL_OWNER]?: {
    isBeneficialOwner: boolean;
  };
};

export type BasePerson = {
  id: number;
  firstName: string;
  lastName: string; // includes middle name if applicable
  dateOfBirth: string; // YYYY-MM-DD format
  email: string;
  gender?: Gender;
  nationalities?: Country[]; // country of citizenship
  countryOfCitizenship?: Country;
  nationality?: Country;
  kycIds: KYC_ID[];
  locations: PersonLocation[];
  // employeeInfo?; // To be implemented later
  // shareholderInfo?; // To be implemented later
  // documentIds?: string[]; // References to associated documents
  onboardingInfo?: PersonOnboardingInfo;
  stakeholderId: string;
  phoneNumber: string;
  // Employment start date for the person (from Director model directly)
  employmentStartDate?: string; // YYYY-MM-DD format
  // Director Identification Number (DIN) for Indian directors (from Director model directly).
  directorIdentificationNumber?: string;
};

export type PersonBusinessEntityInfo = {
  // this should ideally come from ownership model, but we currently store and use adhoc sharePercentage values.
  sharePercentage: number;
  governanceRelationships: GovernanceRelationships;
  jobTitle?: string[];
  startDateOfEmployment?: string; // ISO 8601 date string
  // Whether this director is a resident director of the corporation (jurisdiction-scoped).
  isResidentDirector?: boolean;
};

export type IPerson = BasePerson & {
  businessEntitiesInfo: (PersonBusinessEntityInfo & {
    businessEntityId: number;
    businessEntityName: string;
  })[];
};

export type IPersonOfBusinessEntity = BasePerson & PersonBusinessEntityInfo;

export type IKeyPersonStatus = 'ACTIVE' | 'ARCHIVED';

export type IKeyPersonAssociatedEntity = {
  id: number;
  name: string | null;
  /** Incorporation country; drives the flag glyph on each entity chip. */
  country: Country | null;
};

export type IKeyPersonListItem = {
  /** Director.id under the hood (Director table is not renamed yet). */
  id: number;
  firstName: string | null;
  lastName: string | null;
  primaryEmail: string | null;
  status: IKeyPersonStatus;
  /** ISO 8601 of the most recent write to this KP. Null for legacy rows. */
  lastEditedAt: string | null;
  /** Every CHD-linked corp, ascending id. FE picks first chip + "+N" overflow. */
  associatedEntities: IKeyPersonAssociatedEntity[];
  /** Distinct active role attribute keys across the person's entities. */
  activeAttributeKeys: string[];
};

export type IKeyPersonsListSortBy = 'lastEditedAt' | 'name' | 'status';

export type IKeyPersonsListResponse = {
  items: IKeyPersonListItem[];
  /** Total rows matching the search + status filter, pre-pagination. */
  total: number;
};

/** A key person as seen from a single entity: their active roles AT that entity. */
export type IKeyPersonForEntityRole = {
  assignmentId: string;
  attributeKey: string;
  /** Value-aware label, e.g. "Officer (CEO)" or just "Director". */
  displayLabel: string;
};

export type IKeyPersonForEntity = {
  keyPersonId: number;
  displayName: string;
  roles: IKeyPersonForEntityRole[];
};

export type IKeyPersonsForEntityResponse = {
  items: IKeyPersonForEntity[];
  total: number;
};

export type IKeyPersonDeletePreviewEntity = {
  corporationId: number;
  corporationName: string | null;
  activeRoleKeys: string[];
  activeRoleAssignmentCount: number;
};

export type IKeyPersonDeletePreview = {
  keyPersonId: number;
  profileWillBeEnded: boolean;
  activeRoleAssignmentCount: number;
  entities: IKeyPersonDeletePreviewEntity[];
};

export type IKeyPersonProofStatus = 'VALID' | 'EXPIRING_SOON' | 'EXPIRED';

export type IKeyPersonResidentialAddress = {
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
};

export type IKeyPersonCustomField = {
  key: string;
  value: unknown;
};

export type IKeyPersonRole = {
  assignmentId: string;
  corporationId: number;
  corporationName: string | null;
  attributeKey: string;
  /** Value-aware: the role's chosen value (e.g. "CEO"), else the role label. */
  displayLabel: string;
  startDate: string | null;
  endDate: string | null;
};

export type IKeyPersonRoleAssignment = {
  assignmentId: string;
  corporationId: number;
  corporationName: string | null;
  /** Incorporation country; drives the entity flag. */
  country: string | null;
  attributeKey: string;
  /** Role-bucket label (e.g. "Officer"); not value-aware — see `detail`. */
  label: string;
  /** Chosen value label for value-bearing roles (e.g. "CEO"); null otherwise. */
  detail: string | null;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
};

export type IKeyPersonRoleAssignmentsResponse = {
  items: IKeyPersonRoleAssignment[];
  total: number;
};

export type IKeyPersonEligibleEntity = {
  corporationId: number;
  corporationName: string | null;
  /** Incorporation country; drives the entity flag. */
  country: string | null;
  /** Active role-bucket keys already held at this entity. */
  heldRoleKeys: string[];
  /** Entity holding type. A label to show, not something to derive answers from. Null when unresolvable. */
  holdingType: HoldingType | null;
  /** Governance role option values allowed here (empty ⇒ the entity type did not resolve). */
  allowedGovernanceRoles: string[];
  /**
   * Whether a governance role is picked here at all. A partnership says its governance through the
   * general-partner flag instead, so there is nothing to offer — which is not the same as
   * allowedGovernanceRoles being empty. Optional until the backend that sends it is deployed.
   */
  isGovernanceRoleApplicable?: boolean;
  /**
   * The governing organ the content names for this entity, and what to call a seat on it. Absent
   * where content has no rows for the entity's legal form, or where its form permits several
   * arrangements and names no default.
   */
  governanceStructure?: IGovernanceStructure;
  /**
   * Which of `allowedGovernanceRoles` the structure's officeholder term names. Sent by the backend
   * so no client carries the body-type mapping itself — the moment one did, the two would drift.
   */
  governanceSeatRole?: string;
};

export type IKeyPersonIdentityProof = {
  /** null = computed empty default slot the FE POSTs to fill; non-null = stored. */
  assignmentId: string | null;
  kycId: string | null;
  customLabel: string | null;
  country: string | null;
  documentNumber: string | null;
  expirationDate: string | null;
  fileId: number | null;
  status: IKeyPersonProofStatus;
  isSuggested: boolean;
  /** Proof's country isn't one of the person's current nationalities. */
  isOutsideNationalities: boolean;
};

export type IIdentityDocument = {
  country: string;
  category: string;
  kycId: string;
  docName: string;
  localName: string | null;
  displayLabel: string;
  isDefault: boolean;
  eligibilityNote: string | null;
};

export type AttributePropertyType =
  | 'TEXT'
  | 'URL'
  | 'EMAIL'
  | 'ADDRESS'
  | 'DATE'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'ENUM'
  | 'PASSWORD'
  | 'FILE';

export type AttributeCardinality = 'SINGLE' | 'MULTIPLE';

export type IAttributePropertyOption = {
  id: string;
  value: string;
  label: string;
  isCustom: boolean;
  displayOrder: number;
};

export type IAttributeProperty = {
  id: string;
  qualifiedKey: string;
  unqualifiedKey: string;
  label: string;
  type: AttributePropertyType;
  isRequired: boolean;
  isCustom: boolean;
  /** One-off value, not a reusable property; false when "save for future". */
  isOneOff: boolean;
  displayOrder: number;
  options: IAttributePropertyOption[];
};

export type IAttribute = {
  id: string;
  key: string;
  label: string;
  cardinality: AttributeCardinality;
  isCustom: boolean;
  displayOrder: number;
  properties: IAttributeProperty[];
};

export type IAttributeRegistry = {
  attributes: IAttribute[];
};

export type IKeyPersonDetail = {
  id: number;
  /** The person's company, which is not always the one the reader is looking from. */
  companyId: number;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  nationalities: string[];
  countryOfCitizenship: string | null;
  countryOfResidence: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  residentialAddress: IKeyPersonResidentialAddress | null;
  lastEditedAt: string | null;
  customFields: IKeyPersonCustomField[];
  roles: IKeyPersonRole[];
  identityProofs: IKeyPersonIdentityProof[];
};
